/**
 * A VISTA DO PORTAL DESTA ABA (issue #584; Stories 35.9 e 35.10).
 *
 * Uma pessoa da Genesis com perfil de cliente, gestora de conta de clientes, ou owner
 * (que abre qualquer cliente ativo), vê o portal "como" um cliente. Qual, decide-se aqui, e é por ABA: o cliente escolhido vai
 * em cada pedido ao `/api/portal` (o header; numa `<img>` ou num `<a>`, a query), e o
 * `requirePortalUser` confere-o contra a pessoa da sessão. Nada daqui autoriza: isto só
 * diz ao servidor que vista a aba quer.
 *
 * POR QUE POR ABA, E NÃO POR SESSÃO: quem apresenta o portal da B3 numa aba e
 * consulta outro cliente noutra não pode ver a primeira passar a mostrar o segundo —
 * seria mostrar à B3 os dados de outro cliente. O `sessionStorage` é da aba; o
 * `localStorage` guarda só a ÚLTIMA escolha, que uma aba nova adota uma vez.
 *
 * O usuário do cliente nunca tem vista: o servidor ignora o header para ele.
 */
import { PORTAL_VIEW_HEADER, PORTAL_VIEW_QUERY, type PortalViews } from '@caliber/shared'

const DA_ABA = 'caliber.portal-view'
const A_ULTIMA = 'caliber.portal-view.last'

/**
 * O cliente da vista desta aba, ou `null`. Sem vista própria, a aba adota a última
 * escolha — e fica com ela, para outra aba não a mudar depois.
 *
 * O `try` é porque o storage pode não existir (modo privado, bloqueado): sem ele,
 * não há vista, e a aba fica na Genesis.
 */
export function currentPortalView(): string | null {
  try {
    const daAba = sessionStorage.getItem(DA_ABA)
    if (daAba) return daAba
    const ultima = localStorage.getItem(A_ULTIMA)
    if (ultima) sessionStorage.setItem(DA_ABA, ultima)
    return ultima
  } catch {
    return null
  }
}

/** Escolhe a vista desta aba (e a última, para as abas novas). */
export function choosePortalView(customerId: string): void {
  try {
    sessionStorage.setItem(DA_ABA, customerId)
    localStorage.setItem(A_ULTIMA, customerId)
  } catch {
    // Sem storage, a escolha não sobrevive ao recarregamento — e a aba volta à Genesis.
  }
}

/** Volta à Genesis: sem vista nesta aba, e sem última escolha para as abas novas. */
export function leavePortalView(): void {
  try {
    sessionStorage.removeItem(DA_ABA)
    localStorage.removeItem(A_ULTIMA)
  } catch {
    // Nada a apagar.
  }
}

/** O header da vista para um `fetch` ao `/api/portal`; vazio sem vista. */
export function portalViewHeaders(): Record<string, string> {
  const vista = currentPortalView()
  return vista ? { [PORTAL_VIEW_HEADER]: vista } : {}
}

/**
 * O endereço de uma leitura do portal que não leva header — a `src` de uma foto, o
 * `href` de um download — com a vista na query. Sem vista (ou sem endereço), o mesmo.
 */
export function withPortalView(url: string): string
export function withPortalView(url: string | null): string | null
export function withPortalView(url: string | null): string | null {
  const vista = currentPortalView()
  if (!url || !vista) return url
  const separador = url.includes('?') ? '&' : '?'
  return `${url}${separador}${PORTAL_VIEW_QUERY}=${encodeURIComponent(vista)}`
}

/**
 * A pessoa pode estar na vista deste cliente? O perfil de cliente dela, um cliente de
 * que é gestora de conta, ou qualquer um, se é owner (Story 35.10). É UX: quem decide é
 * o servidor, que também recusa um cliente inativo e lê o gestor de conta e a marca de
 * owner em cada pedido.
 */
export function canViewPortalAs(
  views: PortalViews | null | undefined,
  customerId: string | null,
): boolean {
  if (!views || !customerId) return false
  return (
    views.owner === true ||
    views.clientCustomer?.id === customerId ||
    views.managedCustomers.some((c) => c.id === customerId)
  )
}

/** A pessoa tem alguma vista a escolher? É o que decide se o «Ver como» aparece. */
export function hasPortalViews(views: PortalViews | null | undefined): views is PortalViews {
  return Boolean(
    views && (views.owner === true || views.clientCustomer || views.managedCustomers.length > 0),
  )
}
